// three.js intrinsics are created with createElement (not lowercase JSX) so dev tooling never stamps DOM attributes on them
import { createElement as h3, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { Loader2 } from "lucide-react";
import { api } from "./api";
import { TYPE_COLOR } from "./MaterialMap";

const hex = (h) => { const c = new THREE.Color(h); return [c.r, c.g, c.b]; };
const BASE = [[150, 150, 158], [56, 160, 230], [70, 70, 78], [90, 220, 230], [100, 100, 110]].map((c) => c.map((x) => x / 255));
const SKY = BASE[3];
const ROOF = [0.19, 0.19, 0.21];
const DIM = [0.16, 0.16, 0.18];
const PRESETS = [["N", 0], ["E", 90], ["S", 180], ["W", 270], ["Top", "top"], ["Iso", "iso"]];

// Model frame (x, y=north-ish, z=up) -> three (x, z, -y), centred at the origin.
const parseMesh = (buf) => {
  const dv = new DataView(buf);
  const hl = dv.getUint32(0, true);
  const h = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 4, hl)));
  const o = 4 + hl;
  const q = new Uint16Array(buf, o + h.offsets.pos, h.faces * 9);
  const pos = new Float32Array(h.faces * 9);
  const c = h.span.map((s) => s / 2);
  for (let i = 0; i < q.length; i += 3) {
    const x = (q[i] / 65535) * h.span[0] - c[0], y = (q[i + 1] / 65535) * h.span[1] - c[1], z = (q[i + 2] / 65535) * h.span[2] - c[2];
    pos[i] = x; pos[i + 1] = z; pos[i + 2] = -y;
  }
  return { h, pos, zone: new Int16Array(buf, o + h.offsets.zone, h.faces), mat: new Int16Array(buf, o + h.offsets.mat, h.faces), cls: new Int8Array(buf, o + h.offsets.cls, h.faces) };
};

const faceColor = (d, f, ctx) => {
  const z = d.zone[f], cl = d.cls[f], t = ctx.types[d.mat[f]];
  const vert = cl === 0 || cl === 1;
  const sel = z >= 0 ? ctx.selZ.has(z) : z <= -10 ? ctx.selS.has(-z - 10) : false;
  const any = ctx.selZ.size + ctx.selS.size > 0;
  let col;
  if (ctx.mode === "material") col = vert && t ? hex(TYPE_COLOR[t] || TYPE_COLOR.Other) : BASE[cl] || BASE[0];
  else col = vert && t === "Exclude" ? DIM : z >= 0 ? ctx.zc[z] : z <= -10 || z === -2 ? SKY : ROOF;
  if (any && !sel) return col.map((x) => x * 0.28 + 0.03);
  return sel ? col.map((x) => Math.min(1, x * 1.15 + 0.08)) : col;
};

const Model = ({ d, ctx, onPick }) => {
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(d.pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(d.pos.length), 3));
    g.computeVertexNormals();
    return g;
  }, [d]);
  useEffect(() => {
    const a = geo.getAttribute("color").array;
    for (let f = 0; f < d.h.faces; f++) {
      const c = faceColor(d, f, ctx);
      for (let v = 0; v < 3; v++) a.set(c, f * 9 + v * 3);
    }
    geo.getAttribute("color").needsUpdate = true;
  }, [geo, d, ctx]);
  useEffect(() => () => geo.dispose(), [geo]);
  const click = (e) => {
    if (e.delta > 6) return;
    e.stopPropagation();
    onPick(d.zone[e.faceIndex], e.shiftKey || e.ctrlKey || e.metaKey);
  };
  return h3("mesh", { geometry: geo, onClick: click }, h3("meshLambertMaterial", { vertexColors: true, side: THREE.DoubleSide }));
};

const Lights = () => [h3("ambientLight", { key: "a", intensity: 0.85 }), h3("directionalLight", { key: "k", position: [1, 2, 1.5], intensity: 1.1 }), h3("directionalLight", { key: "f", position: [-1.5, 0.6, -1], intensity: 0.45 })];

const Scene = ({ d, ctx, onPick, preset, ctl }) => [
  ...Lights(),
  h3(Model, { key: "m", d, ctx, onPick }),
  h3(OrbitControls, { key: "c", ref: ctl, makeDefault: true, enableDamping: true, dampingFactor: 0.12 }),
  h3(Rig, { key: "r", d, preset, ctl }),
];

const Rig = ({ d, preset, ctl }) => {
  const { camera, size } = useThree();
  useEffect(() => {
    const R = Math.hypot(...d.h.span) / 2;
    const vf = (camera.fov * Math.PI) / 360, hf = Math.atan(Math.tan(vf) * camera.aspect);
    const r = (R / Math.sin(Math.min(vf, hf))) * 1.05;
    const nd = (d.h.north_deg * Math.PI) / 180;
    let dir;
    if (preset.k === "top") dir = new THREE.Vector3(0, 1, 0.0001);
    else if (preset.k === "iso") dir = new THREE.Vector3(Math.sin(nd + 2.356), 0.75, -Math.cos(nd + 2.356));
    else { const a = nd + (preset.k * Math.PI) / 180; dir = new THREE.Vector3(Math.sin(a), 0.12, -Math.cos(a)); }
    camera.position.copy(dir.normalize().multiplyScalar(r));
    camera.near = r / 500; camera.far = r * 20; camera.updateProjectionMatrix();
    ctl.current?.target.set(0, 0, 0);
    ctl.current?.update();
  }, [preset, d, camera, ctl, size.width, size.height]);
  return null;
};

export const Viewer3D = ({ project, m, mode, selZ, selS, onPick }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [preset, setPreset] = useState({ k: "iso", n: 0 });
  const ctl = useRef(null);
  useEffect(() => {
    let live = true;
    setD(null); setErr(null);
    api.get(`/projects/${project.id}/model/mesh`, { params: { v: m.uploaded_at }, responseType: "arraybuffer" })
      .then((r) => live && setD(parseMesh(r.data))).catch(() => live && setErr("Could not load the 3D mesh"));
    return () => { live = false; };
  }, [project.id, m.uploaded_at]);
  const types = useMemo(() => Object.fromEntries((m.mat_names || []).map((n, i) => [i, (project.material_map || {})[n]]).filter(([, t]) => t)), [m.mat_names, project.material_map]);
  const zc = useMemo(() => m.zones.map((z) => hex(z.color)), [m.zones]);
  const ctx = useMemo(() => ({ mode, selZ, selS, types, zc }), [mode, selZ, selS, types, zc]);
  return (
    <div onPointerDownCapture={(e) => e.shiftKey && e.preventDefault()} className="relative h-[460px] select-none overflow-hidden rounded-lg border border-zinc-800 bg-[#0c0c0f] md:h-[520px]" data-testid="model-viewer-3d">
      {!d && <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-400" data-testid="model-viewer-loading">{err || <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading 3D model…</>}</div>}
      {d && (
        <Canvas camera={{ fov: 40 }} onPointerMissed={(e) => e.type === "click" && onPick(null, false)} dpr={[1, 2]} data-testid="model-viewer-canvas">
          {h3(Scene, { d, ctx, onPick, preset, ctl })}
        </Canvas>
      )}
      <div className="absolute left-2 top-2 flex flex-wrap gap-1" data-testid="model-viewer-presets">
        {PRESETS.map(([l, k]) => (
          <button key={l} data-testid={`model-view-preset-${l.toLowerCase()}`} onClick={() => setPreset((p) => ({ k, n: p.n + 1 }))}
            className="rounded border border-zinc-700 bg-zinc-900/80 px-2 py-0.5 font-mono text-[10px] text-zinc-300 backdrop-blur transition-colors hover:border-sky-500/60 hover:text-white">{l}</button>
        ))}
      </div>
      <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-zinc-950/70 px-2 py-1 font-mono text-[10px] text-zinc-400">
        Drag rotate · scroll/pinch zoom · right-drag/2-finger pan · tap a face to select its band · Shift/Ctrl adds
      </div>
    </div>
  );
};
